import { useEffect, useState } from 'react';
import { AlertBanner, ErrorState, LoadingState } from '@assist/ui';
import {
  endpoints,
  ApiError,
  type ProcessingRegisterResponse,
} from '../state/api.js';
import { useSession } from '../state/session.js';

/**
 * Layar Daftar Pemrosesan — M15 · rute `/processing-register` · izin PUBLIC.
 *
 * KENAPA HALAMAN INI PUBLIC, DAN KENAPA ITU BUKAN KELONGGARAN
 * ----------------------------------------------------------
 * Yang membacanya adalah organisasi yang sedang MEMUTUSKAN apakah akan menandatangani
 * perjanjian pemrosesan data (UU 27/2022 Pasal 51). Sebuah daftar pemrosesan yang
 * hanya dapat dibaca setelah berlangganan bukan dasar untuk memutuskan — ia brosur.
 * Halaman ini juga yang dibuka petugas kepatuhan pelanggan pada rapat uji tuntas,
 * dan pada saat itu ia belum punya akun.
 *
 * Yang dikirim server hanyalah KODE — `WORK_AREA_PHOTO`, `OHS_COMPLIANCE`,
 * `LEGAL_OBLIGATION` — dan tidak satu pun baris data, tidak satu pun nama organisasi.
 *
 * KENAPA ISINYA TIDAK DITULIS DI SINI
 * -----------------------------------
 * Karena daftar yang ditulis tangan akan benar pada hari ia ditulis dan salah tiga
 * sprint kemudian, dan yang salah bukan dokumentasinya melainkan sebuah pernyataan
 * yang sudah DITANDATANGANI dan yang pelanggan andalkan untuk memenuhi kewajibannya
 * sendiri kepada pekerjanya. Isinya dirakit dari `processing_basis` di manifest modul
 * — sumber yang sama dengan gerbang CI dan dengan dasar yang dicap basis data ke
 * setiap baris riwayat. Bila ketiganya berpisah, gerbang `assert-dasar-pemrosesan.sh`
 * menggagalkan build.
 *
 * DUA BAGIAN YANG SENGAJA MENGATAKAN "BELUM DINYATAKAN"
 * ----------------------------------------------------
 * Lokasi pemrosesan (butir P9, Pasal 56) dan daftar subprosesor (butir P10) kosong
 * pada rilis ini, karena penyedia awannya belum diputuskan pemilik proyek. Keduanya
 * TETAP ditampilkan, dengan kalimat yang menyatakan bahwa keputusannya belum diambil.
 * Menghilangkan bagiannya akan terbaca sebagai "tidak ada subprosesor" — dan itu
 * pernyataan yang berbeda, serta pernyataan yang tidak benar.
 */
export function ProcessingRegister() {
  const { t } = useSession();
  const [data, setData] = useState<ProcessingRegisterResponse | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    endpoints
      .processingRegister()
      .then((res) => alive && setData(res))
      .catch(
        (err: unknown) =>
          alive && setFailed(err instanceof ApiError ? err.problem.code : 'INTERNAL'),
      );
    return () => {
      alive = false;
    };
  }, []);

  if (failed) {
    return (
      <ErrorState
        title={t('state.error.title')}
        body={t('state.error.body')}
        code={failed}
        retryLabel={t('app.retry')}
        onRetry={() => window.location.reload()}
      />
    );
  }
  if (!data) return <LoadingState rows={6} label={t('app.loading')} />;

  return (
    <section className="assist-privacy">
      <h1>{t('register.title')}</h1>
      <p>{t('register.subtitle')}</p>

      <h2>{t('register.categoryHeading')}</h2>
      <ul className="assist-consent-list">
        {data.categories.map((c) => (
          <li className="assist-consent-list__item" key={c.code}>
            <strong>{t(`privacy.category.${c.code}`)}</strong>
            <span>{c.purposes.map((p) => t(`privacy.purpose.${p}`)).join(' · ')}</span>
            <span className="assist-privacy__meta">
              {c.legal_bases.map((b) => t(`privacy.basis.${b}`)).join(' · ')}
            </span>
          </li>
        ))}
      </ul>

      <h2>{t('register.basisHeading')}</h2>
      <ul className="assist-consent-list">
        {data.legal_bases.map((b) => (
          <li className="assist-consent-list__item" key={b.code}>
            <strong>{t(`privacy.basis.${b.code}`)}</strong>
            <span className="assist-privacy__meta">
              {t('register.writeOperations', { n: b.write_operations })}
            </span>
          </li>
        ))}
      </ul>

      <h2>{t('register.moduleHeading')}</h2>
      <ul className="assist-consent-list">
        {data.modules.map((m) => (
          <li className="assist-consent-list__item" key={m.code}>
            <strong>{m.name}</strong>
            <span className="assist-privacy__meta">
              {t('register.writeOperations', { n: m.write_operations })}
            </span>
            <span>{m.categories.map((c) => t(`privacy.category.${c}`)).join(' · ')}</span>
          </li>
        ))}
      </ul>

      <h2>{t('register.locationHeading')}</h2>
      {data.locations_declared ? (
        <ul className="assist-consent-list">
          {data.processing_locations.map((l) => (
            <li className="assist-consent-list__item" key={l.aspect}>
              <strong>{t(`privacy.location.${l.aspect}`)}</strong>
              <span>{`${l.region} · ${l.country}`}</span>
            </li>
          ))}
        </ul>
      ) : (
        <AlertBanner
          kind="information"
          signalWord={t('signal.information')}
          title={t('register.location.undeclared')}
        />
      )}

      <h2>{t('register.subprocessorHeading')}</h2>
      {data.subprocessors_declared ? (
        <ul className="assist-consent-list">
          {data.subprocessors.map((sp) => (
            <li className="assist-consent-list__item" key={sp.name}>
              <strong>{sp.name}</strong>
              <span>{`${sp.role} · ${sp.country}`}</span>
            </li>
          ))}
        </ul>
      ) : (
        <AlertBanner
          kind="information"
          signalWord={t('signal.information')}
          title={t('register.subprocessor.none')}
        />
      )}

      {/*
        Yang TIDAK diproses, dinyatakan dengan sengaja. Sebuah daftar yang hanya
        menyebut apa yang diambil membiarkan pembacanya menduga sisanya, dan yang
        paling ditakuti pelanggan besar — nomor induk kependudukan, data kesehatan,
        gaji, pengenalan wajah — justru tidak ada di sini. Menuliskannya adalah
        pernyataan yang dapat dipegang, dan ia dapat diuji: bila sebuah kategori itu
        kelak masuk manifest, gerbang CI menuntut kalimatnya dan telaah ini terjadi
        lagi.
      */}
      <h2>{t('register.notCollected')}</h2>
      <p>{t('register.notCollected.body')}</p>
    </section>
  );
}
